<template>
    <modal ref="prompt">
        <form @submit.prevent="save">
            <div class="modal-body">
                <div class="mb-3">
                    <label class="form-label">Name</label>
                    <input type="text" required class="form-control" v-model="name" />
                </div>
                <div class="mb-0">
                    <label class="form-label">Text</label>
                    <textarea rows="15" class="form-control" v-model="text"></textarea>
                </div>
                <div class="mb-0 mt-3">
                    <label class="form-label">Documents</label>
                    <input
                        type="file"
                        class="form-control"
                        ref="documentsInput"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        @change="onDocumentsChange">
                    <div class="form-text">
                        Supports .pdf, .doc/.docx and .xls/.xlsx files
                    </div>
                    <button
                        type="button"
                        class="btn btn-outline-primary mt-3"
                        :disabled="uploadingDocuments || !selectedDocuments.length"
                        @click="uploadDocuments">
                        <span v-if="uploadingDocuments" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span>{{ uploadingDocuments ? "Uploading..." : "Upload documents" }}</span>
                    </button>
                </div>
                <div class="mt-4">
                    <div v-if="filesLoading" class="small text-muted">Loading documents...</div>
                    <div v-else-if="!knowledgeFiles.length" class="small text-muted">No documents uploaded yet.</div>
                    <ul v-else class="list-group list-group-flush">
                        <li
                            v-for="file in knowledgeFiles"
                            :key="file.id"
                            class="list-group-item px-0 d-flex justify-content-between align-items-start gap-2">
                            <div>
                                <div class="fw-semibold">{{ file.name }}</div>
                                <div class="small text-muted">
                                    <span>{{ file.type }}</span>
                                    <span class="mx-1">·</span>
                                    <span>{{ formatFileSize(file.size) }}</span>
                                    <span class="mx-1">·</span>
                                    <span>{{ formatModified(file.modified) }}</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                :disabled="isDeletingFile(file.id)"
                                @click="deleteFile(file)">
                                <span v-if="isDeletingFile(file.id)" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                <span>{{ isDeletingFile(file.id) ? "Deleting..." : "Delete" }}</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="modal-footer">
                <button class="btn btn-primary" type="submit" :disabled="disableSave">{{item ? 'Edit' : 'Create'}}</button>
            </div>
        </form>
    </modal>
</template>
<script>
import api from "@/common/api";
import filters from "@/common/filters";

export default {
    props: {
        onDocumentsUploaded: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            callback: null,
            loading: false,
            name: "",
            text: "",
            item: null,
            selectedDocuments: [],
            uploadingDocuments: false,
            knowledgeFiles: [],
            filesLoading: false,
            deletingFileIds: []
        };
    },
    computed: {
        disableSave() {
            return this.loading || this.uploadingDocuments || this.selectedDocuments.length > 0;
        }
    },
    created() {
    },
    methods: {
        save() {
            if (this.loading) {
                return;
            }

            let method = api.knowledgeCreate;
            if (this.item && this.item.id) {
                method = api.knowledgeUpdate;
            }

            this.loading = true;
            method({ id: this.item ? this.item.id : 0, name: this.name, text: this.text }).then((item) => {
                this.item = item;
                this.flashSuccess(method === api.knowledgeCreate ? "Knowledge base created" : "Knowledge base updated");
                this.close();
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.loading = false;
            });
        },
        open(callback) {
            this.item = null;
            this.name = "";
            this.text = "";
            this.resetDocumentsInput();
            this.knowledgeFiles = [];
            this.callback = callback;
            this.$refs.prompt.open();
            this.loadFiles();
        },
        openEdit(item, callback) {
            this.item = item;
            this.name = item.name;
            this.text = item.text;
            this.resetDocumentsInput();
            this.knowledgeFiles = [];
            this.callback = callback;
            this.$refs.prompt.open();
            this.loadFiles();
        },
        close() {
            if (this.callback && this.item) {
                this.callback(this.item, () => {
                    this.$refs.prompt.close();
                });
            } else {
                this.$refs.prompt.close();
            }
        },
        onDocumentsChange(event) {
            this.selectedDocuments = Array.from(event.target.files || []);
        },
        uploadDocuments() {
            if (!this.selectedDocuments.length || this.uploadingDocuments) {
                return;
            }
            const allowedExtensions = /\.(pdf|doc|docx|xls|xlsx)$/i;
            const invalidFiles = this.selectedDocuments.filter(file => !allowedExtensions.test(file.name));
            if (invalidFiles.length) {
                this.flashError(`Unsupported file type: ${invalidFiles.map(file => file.name).join(", ")}`);
                this.resetDocumentsInput();
                return;
            }

            this.uploadingDocuments = true;
            api.knowledgeUpload(this.selectedDocuments, this.currentKnowledgeId()).then(() => {
                this.flashSuccess("Knowledge base document uploaded");
                if (typeof this.onDocumentsUploaded === "function") {
                    this.onDocumentsUploaded();
                }
                this.$emit("documents-uploaded");
                this.loadFiles();
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.uploadingDocuments = false;
                this.resetDocumentsInput();
            });
        },
        loadFiles() {
            this.filesLoading = true;
            api.knowledgeFiles(this.currentKnowledgeId()).then(files => {
                this.knowledgeFiles = Array.isArray(files) ? files : [];
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.filesLoading = false;
            });
        },
        deleteFile(file) {
            if (this.isDeletingFile(file.id)) {
                return;
            }

            this.deletingFileIds.push(file.id);
            api.knowledgeDeleteFile(file.id, this.currentKnowledgeId()).then(() => {
                this.flashSuccess("Knowledge base document deleted");
                this.knowledgeFiles = this.knowledgeFiles.filter(f => f.id !== file.id);
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.deletingFileIds = this.deletingFileIds.filter(id => id !== file.id);
            });
        },
        resetDocumentsInput() {
            this.selectedDocuments = [];
            if (this.$refs.documentsInput) {
                this.$refs.documentsInput.value = "";
            }
        },
        currentKnowledgeId() {
            return this.item && this.item.id ? this.item.id : 0;
        },
        isDeletingFile(fileId) {
            return this.deletingFileIds.includes(fileId);
        },
        formatFileSize(size) {
            return filters.formatFileSize(size);
        },
        formatModified(value) {
            const timestampMs = typeof value === "number" ? value * 1000 : value;
            return filters.formatDate(timestampMs, "compact", true);
        }
    }
};
</script>
